/**
 * `byLineKind` table — formatting only; no truth claims from combined fields. TMMPDM-0037: Retail ·
 * Wholesale (our price) · Reseller margin · Reseller net; no Uplift or Platform share (tmm-api 0099).
 */
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { Amount } from '@/components/ui';
import type { KindTotals, RevenueLineKind } from '@/api/tmm/types';

interface LineKindBreakdownProps {
  rows: KindTotals[];
  /** `null` when the tenant's currency is unknown — amounts render `—`. */
  currency: string | null;
  locale: string;
}

const LINE_FALLBACK: Record<RevenueLineKind, string> = {
  BASE: 'Subscriptions',
  SEAT: 'Guest seats',
  CONNECTED: 'Connected accounts',
  AI: 'AI',
  TAX: 'Tax',
};

export function LineKindBreakdown({ rows, currency, locale }: LineKindBreakdownProps) {
  const { t } = useCopy();
  const money = (minor: number) =>
    currency === null ? '—' : <Amount minor={minor} currency={currency} locale={locale} />;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label={t('revenue.breakdownAria', 'Revenue by line kind')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('revenue.col.line', 'Line')}</TableCell>
            <TableCell align="right">{t('revenue.col.retail', 'Retail')}</TableCell>
            <TableCell align="right">
              {t('revenue.col.wholesale', 'Wholesale (our price)')}
            </TableCell>
            <TableCell align="right">
              {t('revenue.col.resellerMargin', 'Reseller margin')}
            </TableCell>
            <TableCell align="right">
              {t('revenue.col.resellerNet', 'Reseller net (after affiliates)')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.line}>
              <TableCell>{t(`revenue.line.${row.line}`, LINE_FALLBACK[row.line])}</TableCell>
              <TableCell align="right">{money(row.retailMinor)}</TableCell>
              <TableCell align="right">{money(row.wholesaleMinor)}</TableCell>
              <TableCell align="right">{money(row.resellerMarginMinor)}</TableCell>
              <TableCell align="right">{money(row.tenantShareMinor)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
